import React, { useEffect, useState } from "react";
import Link from "next/link";

/**
 * Cross-site promotion for the Founding Member offer (/founding-members).
 *
 * The offer page itself is server-rendered with a live count; every other
 * marketing page is static, so the count is fetched after hydration and the
 * copy stays truthful until it arrives ("100 spots total", never "100 left").
 *
 * One fetch implementation lives here and `FoundingMemberBanner` uses it too —
 * three copies of the same request would drift the moment the shape changes.
 */

export type FoundingRemaining = {
  remaining: number;
  total: number;
  soldOut: boolean;
} | null;

export function useFoundingRemaining(): FoundingRemaining {
  const [state, setState] = useState<FoundingRemaining>(null);

  useEffect(() => {
    let active = true;
    fetch("/api/founding-members/availability")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active) return;
        if (typeof data?.remaining === "number" && typeof data?.total === "number")
          setState({
            remaining: data.remaining,
            total: data.total,
            soldOut: Boolean(data.soldOut),
          });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return state;
}

/** "43 of 100 spots remain" — or a spot-count-free line before it loads. */
export function foundingSpotsLabel(availability: FoundingRemaining) {
  if (!availability) return "100 spots total";
  if (availability.soldOut || availability.remaining <= 0)
    return `All ${availability.total} spots are claimed`;
  return `${availability.remaining} of ${availability.total} spots remain`;
}

/**
 * The card form, for pages where the offer deserves a real block rather than
 * a strip: /pricing (where the monthly ladder otherwise reads as the only
 * option) and the /vs comparisons (where "forever monthly" is the contrast).
 * `note` carries the honest, page-specific line.
 */
export default function FoundingMemberCallout({ note }: { note: string }) {
  const availability = useFoundingRemaining();
  const soldOut = availability?.soldOut === true;

  return (
    <div className="overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-violet-50 shadow-sm">
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:gap-7 sm:p-7">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center rounded-full bg-amber-400 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.13em] text-amber-950">
            Founding Member offer
          </span>
          <h2 className="mt-3 text-[21px] font-extrabold tracking-tight text-slate-900 sm:text-[24px]">
            Never pay monthly for a CRM again.
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
            $397 once — one payment, no recurring FrazzleCRM subscription.
            Limited to 100 Founding Members, then the offer closes. {note}
          </p>
          <p className="mt-2.5 text-[12.5px] font-bold text-violet-700" aria-live="polite">
            {foundingSpotsLabel(availability)}
          </p>
        </div>
        <Link
          href="/founding-members"
          className="inline-flex min-h-[48px] shrink-0 items-center justify-center rounded-xl bg-slate-900 px-6 py-3 text-center text-[14px] font-extrabold text-white shadow-lg shadow-slate-900/15 hover:bg-slate-800"
        >
          {soldOut ? "See the offer" : "See the Founding Member offer"}
        </Link>
      </div>
    </div>
  );
}
